import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Container,
    Row,
    Col,
    Form,
    InputGroup,
    Button,
    Pagination,
} from 'react-bootstrap';
import axios from 'axios';
import CustomNavbar from "../components/Navbar";
import Footer from "../components/Footer";
import NewsCard from "../components/NewsCard";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../seo/pages";

const PAGE_SIZE = 12;

const MONTHS = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
];

function buildPageWindow(current, total) {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages = new Set([1, total, current, current - 1, current + 1, current - 2, current + 2]);
    const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < sorted.length; i++) {
        out.push(sorted[i]);
        if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
            out.push(`ellipsis-${sorted[i]}`);
        }
    }
    return out;
}

function NewsPage() {
    const [newsItems, setNewsItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const [queryInput, setQueryInput] = useState('');
    const [query, setQuery] = useState('');
    const [year, setYear] = useState('all');
    const [month, setMonth] = useState('all');
    const [page, setPage] = useState(1);

    const sectionRef = useRef(null);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_API_URL}/api/news?sort=Date:desc&populate=*&pagination[pageSize]=1000`
                );
                const mappedNews = res.data.data.map((item) => {
                    const r = item.attributes || item;
                    const unique = r.unique ?? item.unique;
                    const id = item.id;
                    const dateObj = r.Date ? new Date(r.Date) : null;
                    return {
                        id,
                        title: r.Title,
                        content: r.Content,
                        date: r.Date,
                        year: dateObj ? String(dateObj.getUTCFullYear()) : null,
                        month: dateObj ? String(dateObj.getUTCMonth() + 1) : null,
                        category: r.CategoryName || "News",
                        link: unique ? `/news/${unique}` : `/news/${id}`,
                    };
                });

                setNewsItems(mappedNews);
            } catch (error) {
                console.error('Error fetching news:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchNews();
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setQuery(queryInput.trim().toLowerCase()), 200);
        return () => clearTimeout(t);
    }, [queryInput]);

    useEffect(() => {
        setPage(1);
    }, [query, year, month]);

    useEffect(() => {
        if (year === 'all' && month !== 'all') {
            setMonth('all');
        }
    }, [year, month]);

    const years = useMemo(() => {
        const set = new Set();
        newsItems.forEach((n) => n.year && set.add(n.year));
        return [...set].sort((a, b) => Number(b) - Number(a));
    }, [newsItems]);

    const filtered = useMemo(() => {
        return newsItems.filter((n) => {
            if (query && !(n.title || '').toLowerCase().includes(query)) return false;
            if (year !== 'all' && n.year !== year) return false;
            if (month !== 'all' && n.month !== month) return false;
            return true;
        });
    }, [newsItems, query, year, month]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount);
    const paged = useMemo(
        () => filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
        [filtered, safePage]
    );

    const goToPage = (next) => {
        if (next < 1 || next > pageCount || next === safePage) return;
        setPage(next);
        if (sectionRef.current) {
            sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const clearFilters = () => {
        setQueryInput('');
        setQuery('');
        setYear('all');
        setMonth('all');
    };

    const hasActiveFilters = query !== '' || year !== 'all' || month !== 'all';
    const total = filtered.length;
    const rangeStart = total === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(total, safePage * PAGE_SIZE);

    const pageWindow = buildPageWindow(safePage, pageCount);

    return (
        <div id="news">
            <Seo {...PAGE_SEO.news} />
            <CustomNavbar />
            <section
                className="news-page-banner text-center text-white"
                style={{ backgroundImage: "url('/images/new-banner-new.png')" }}
            >
                <h2>News & Events</h2>
            </section>
            <section className="news-section py-5" ref={sectionRef}>
                <Container>
                    <div className="d-flex flex-wrap align-items-end justify-content-between mb-3 news-archive-header">
                        <h3 className="mb-0">News Archive</h3>
                        <p
                            className="news-archive-count mb-0"
                            aria-live="polite"
                        >
                            {loading
                                ? 'Loading...'
                                : total === 0
                                    ? 'No results'
                                    : `Showing ${rangeStart}\u2013${rangeEnd} of ${total}`}
                        </p>
                    </div>

                    <Row className="news-archive-controls g-3 mb-4">
                        <Col xs={12} md={6}>
                            <Form.Label htmlFor="news-search" className="news-archive-label">
                                Search
                            </Form.Label>
                            <InputGroup>
                                <InputGroup.Text aria-hidden="true">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="7" cy="7" r="5" />
                                        <line x1="11" y1="11" x2="14" y2="14" strokeLinecap="round" />
                                    </svg>
                                </InputGroup.Text>
                                <Form.Control
                                    id="news-search"
                                    type="search"
                                    placeholder="Search news releases"
                                    value={queryInput}
                                    onChange={(e) => setQueryInput(e.target.value)}
                                    aria-label="Search news"
                                />
                                {queryInput && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setQueryInput('')}
                                        aria-label="Clear search"
                                    >
                                        ×
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label htmlFor="news-year" className="news-archive-label">
                                Year
                            </Form.Label>
                            <Form.Select
                                id="news-year"
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                            >
                                <option value="all">All years</option>
                                {years.map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Label htmlFor="news-month" className="news-archive-label">
                                Month
                            </Form.Label>
                            <Form.Select
                                id="news-month"
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                                disabled={year === 'all'}
                            >
                                <option value="all">All months</option>
                                {MONTHS.map((m) => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>

                    {loading ? (
                        <p className="news-archive-empty text-center">Loading latest news...</p>
                    ) : total === 0 ? (
                        <div className="news-archive-empty text-center">
                            <p className="mb-3">No news matches your search.</p>
                            {hasActiveFilters && (
                                <Button
                                    variant="outline-dark"
                                    onClick={clearFilters}
                                    className="news-archive-clear"
                                >
                                    Clear filters
                                </Button>
                            )}
                        </div>
                    ) : (
                        <Row className="g-4">
                            {paged.map((news) => (
                                <Col key={news.id} md={6} lg={4}>
                                    <NewsCard news={news} />
                                </Col>
                            ))}
                        </Row>
                    )}

                    {!loading && pageCount > 1 && (
                        <Pagination className="news-archive-pagination justify-content-center mt-4 mb-0">
                            <Pagination.First
                                onClick={() => goToPage(1)}
                                disabled={safePage === 1}
                                aria-label="Go to first page"
                            />
                            <Pagination.Prev
                                onClick={() => goToPage(safePage - 1)}
                                disabled={safePage === 1}
                                aria-label="Go to previous page"
                            />
                            {pageWindow.map((p) =>
                                typeof p === 'number' ? (
                                    <Pagination.Item
                                        key={p}
                                        active={p === safePage}
                                        onClick={() => goToPage(p)}
                                        aria-label={p === safePage ? 'Current page' : `Go to page ${p}`}
                                    >
                                        {p}
                                    </Pagination.Item>
                                ) : (
                                    <Pagination.Ellipsis key={p} disabled />
                                )
                            )}
                            <Pagination.Next
                                onClick={() => goToPage(safePage + 1)}
                                disabled={safePage === pageCount}
                                aria-label="Go to next page"
                            />
                            <Pagination.Last
                                onClick={() => goToPage(pageCount)}
                                disabled={safePage === pageCount}
                                aria-label="Go to last page"
                            />
                        </Pagination>
                    )}
                </Container>
            </section>
            <Footer />
        </div>
    );
}

export default NewsPage;
